/**
 * Keeper Brand Lift Survey client.
 * Base path: /v1/bls  (AdminBootloader prefix + BrandLiftSurveyController).
 *
 * PUT  /v1/bls/{campaignId}  create (multipart)
 * GET  /v1/bls/list/{campaignId}
 * GET  /v1/bls/{id}
 * POST /v1/bls/{id}          update (multipart)
 * DELETE /v1/bls/{id}
 * GET  /v1/bls/{id}/survey   HTML preview
 * GET  /v1/bls/begin/{id}    write KV flight config
 * GET  /v1/bls/start/{id}    entity status active
 * GET  /v1/bls/pause/{id}
 * GET  /v1/bls/report/{id}
 * GET  /v1/bls/report/dailyppl/{id}
 * GET  /v1/bls/optimization-log/{id}
 * GET  /v1/bls/optimizeBLSs?campaign={id}  run the optimizer for one campaign (non-prod)
 * GET/POST/DELETE /v1/bls/test-data/{id}  fake warehouse rows (non-prod)
 * GET  /share/bls?sharedToken=  read-only report for a shared reporting link
 */

import axios from 'axios';
import { unwrapEntity, unwrapList } from './bls/payload.js';

const API_URL = process.env.VUE_APP_API_URL;
const REQUEST_TIMEOUT = 180000;

function request(config) {
  return axios({
    timeout: REQUEST_TIMEOUT,
    withCredentials: true,
    ...config,
  });
}

function rejectIfFailed(response, fallbackMessage) {
  const body = response?.data;
  const status = body?.status ?? response?.status;
  if (status && Number(status) >= 400) {
    const error = new Error(body?.message || fallbackMessage);
    error.response = response;
    throw error;
  }
  return body;
}

const BLSService = {
  getSharedReport(sharedToken) {
    return request({
      method: 'GET',
      url: `${API_URL}/share/bls`,
      params: { sharedToken },
    }).then((res) => rejectIfFailed(res, 'Could not load Brand Lift results'));
  },

  getSharedSurveyList(sharedToken) {
    return request({
      method: 'GET',
      url: `${API_URL}/share/bls`,
      params: { sharedToken, listOnly: 1 },
    }).then((res) => rejectIfFailed(res, 'Could not load Brand Lift results'));
  },

  listByCampaign(campaignId) {
    return request({ method: 'GET', url: `${API_URL}/v1/bls/list/${campaignId}` }).then((res) =>
      unwrapList(rejectIfFailed(res, 'Could not list Brand Lift Surveys'))
    );
  },

  get(id) {
    return request({ method: 'GET', url: `${API_URL}/v1/bls/${id}` }).then((res) =>
      unwrapEntity(rejectIfFailed(res, 'Could not load Brand Lift Survey'))
    );
  },

  create(campaignId, formData) {
    return request({
      method: 'PUT',
      url: `${API_URL}/v1/bls/${campaignId}`,
      data: formData,
    }).then((res) => {
      const entity = unwrapEntity(rejectIfFailed(res, 'Could not create Brand Lift Survey'));
      if (!entity?.id) throw new Error('Keeper did not return a Brand Lift Survey id.');
      return entity;
    });
  },

  update(id, formData) {
    return request({
      method: 'POST',
      url: `${API_URL}/v1/bls/${id}`,
      data: formData,
    }).then((res) => unwrapEntity(rejectIfFailed(res, 'Could not update Brand Lift Survey')));
  },

  remove(id) {
    return request({ method: 'DELETE', url: `${API_URL}/v1/bls/${id}` }).then((res) =>
      rejectIfFailed(res, 'Could not delete Brand Lift Survey')
    );
  },

  /**
   * Writes Cloudflare KV. Backend may leave entity status as draft.
   * Call start() afterwards so the campaign card shows active.
   */
  begin(id) {
    return request({ method: 'GET', url: `${API_URL}/v1/bls/begin/${id}` }).then((res) =>
      unwrapEntity(rejectIfFailed(res, 'Could not begin Brand Lift Survey'))
    );
  },

  start(id) {
    return request({ method: 'GET', url: `${API_URL}/v1/bls/start/${id}` }).then((res) =>
      unwrapEntity(rejectIfFailed(res, 'Could not start Brand Lift Survey'))
    );
  },

  pause(id) {
    return request({ method: 'GET', url: `${API_URL}/v1/bls/pause/${id}` }).then((res) =>
      unwrapEntity(rejectIfFailed(res, 'Could not pause Brand Lift Survey'))
    );
  },

  getSurveyHtml(id) {
    return request({
      method: 'GET',
      url: `${API_URL}/v1/bls/${id}/survey`,
      responseType: 'text',
    }).then((res) => res.data);
  },

  getReport(id) {
    return request({ method: 'GET', url: `${API_URL}/v1/bls/report/${id}` }).then((res) =>
      rejectIfFailed(res, 'Could not load Brand Lift report')
    );
  },

  getDailyPpl(id) {
    return request({ method: 'GET', url: `${API_URL}/v1/bls/report/dailyppl/${id}` }).then((res) =>
      rejectIfFailed(res, 'Could not load daily Brand Lift series')
    );
  },

  optimizeBLSs(campaignId) {
    return request({
      method: 'GET',
      url: `${API_URL}/v1/bls/optimizeBLSs`,
      params: { campaign: campaignId },
      timeout: 300000,
    }).then((res) => rejectIfFailed(res, 'Could not run Brand Lift optimization'));
  },

  getOptimizationLog(id) {
    return request({ method: 'GET', url: `${API_URL}/v1/bls/optimization-log/${id}` }).then((res) =>
      rejectIfFailed(res, 'Could not load Brand Lift optimization log')
    );
  },

  getTestData(id) {
    return request({ method: 'GET', url: `${API_URL}/v1/bls/test-data/${id}` }).then((res) =>
      rejectIfFailed(res, 'Could not load Brand Lift test data status')
    );
  },

  insertTestData(id, payload) {
    return request({
      method: 'POST',
      url: `${API_URL}/v1/bls/test-data/${id}`,
      data: payload,
      timeout: 300000,
    }).then((res) => rejectIfFailed(res, 'Could not insert Brand Lift test numbers'));
  },

  wipeTestData(id) {
    return request({
      method: 'DELETE',
      url: `${API_URL}/v1/bls/test-data/${id}`,
      timeout: 300000,
    }).then((res) => rejectIfFailed(res, 'Could not remove Brand Lift test numbers'));
  },
};

export default BLSService;
